import { MonthlyActivity, MonthlyBucket, MonthlyCharge, MonthlyEvidence, MonthlyQuestion, MonthlyRecommendation, MonthlyRuleId, PricingMonthlyRow } from './api';
import { chargeTerms, MONTHLY_RULES, monthlyUsd } from './monthlySimpleMath';
import { monthlyTariffOptions } from './monthlyTariffs';

const bucketLabels: Record<MonthlyBucket, string> = {
  communication: 'Communication', processing: 'Processing',
  infrastructure: 'Platform & infrastructure', third_party: 'Third-party services',
};

/** Short policy names are presentation only. Stored labels and agreements are untouched. */
export function monthlyChargeLabel(charge: MonthlyCharge): string {
  if (charge.ruleId === 'phase_addon' || charge.ruleId === 'phase_user' || charge.ruleId === 'third_party' || charge.ruleId === 'third_party_record') return charge.label;
  if (charge.label.trim() && charge.label.length <= 72 && !/[\r\n]/.test(charge.label)) return charge.label;
  return monthlyTariffOptions(MONTHLY_RULES[charge.ruleId].bucket).find(option => option.value === charge.ruleId)?.label
    ?? charge.label;
}

export function monthlyChargePrice(charge: MonthlyCharge): string {
  const terms = chargeTerms(charge);
  const prefix = `${charge.label}: `;
  return terms.startsWith(prefix) ? terms.slice(prefix.length) : terms;
}

export function monthlyChargeTerms(charge: MonthlyCharge): string {
  return `${monthlyChargeLabel(charge)}: ${monthlyChargePrice(charge)}`;
}

export function monthlyRowLabel(row: PricingMonthlyRow): string {
  if (!row.simple || row.model !== 'simple') return row.label;
  if (row.displayLabel !== undefined) return row.displayLabel;
  const only = row.simple.charges.length === 1 ? row.simple.charges[0] : null;
  if (only?.ruleId === 'phase_addon' || only?.ruleId === 'phase_user') return row.label;
  return bucketLabels[row.simple.bucket];
}

export function monthlyQuestionLabel(question: MonthlyQuestion, rows: PricingMonthlyRow[]): string {
  const split = question.key.lastIndexOf('.');
  const charge = rows.flatMap(row => row.simple?.charges ?? []).find(item => item.id === question.key.slice(0, split));
  if (!charge) return question.label;
  const field = question.key.slice(split + 1);
  const suffix: Record<string, string> = {
    quantity: 'example volume', minimum: 'monthly minimum', manualRate: 'agreed rate', unitCost: 'provider cost',
  };
  return suffix[field] ? `${monthlyChargeLabel(charge)} · ${suffix[field]}` : question.label;
}

/** Never turn a minimum or a number embedded in prose into an example total. */
export function monthlyExampleLabel(example: MonthlyRecommendation['exampleMonth']): string {
  return typeof example.total === 'number' && Number.isFinite(example.total) && example.total >= 0
    ? `${monthlyUsd(example.total)} / month` : 'Complete monthly details';
}

const ruleReasons: Record<MonthlyRuleId, string> = {
  sms: 'Unlimited two-way SMS, charged per monthly contact.',
  email: 'Unlimited email, charged per monthly contact.',
  sms_email: 'One rate covers both SMS and email for each active CRM lead.',
  voice: 'Calls are priced in two-minute units, with an agreed monthly minimum.',
  mailbox: 'A monthly rate for reading all inbound mail in each mailbox.',
  processing_heavy: 'The standard rate for document-heavy work per active unit.',
  processing_light: 'The standard rate for light, bounded work per active unit.',
  generated_document: 'One output combines several source documents.',
  platform_500: 'The platform tier for one system, dashboard and logs.',
  platform_750: 'The platform tier for multiple systems, storage, telephony or private hosting.',
  platform_850: 'The platform tier for three to five systems with pipelines or heavy sync.',
  hosting_500: 'A fixed monthly hosting package.',
  hosting_750: 'A fixed monthly hosting package at the higher tier.',
  ap_unlimited: 'A fixed package for unlimited accounts-payable invoices.',
  secondary_unlimited: 'A fixed package for an additional document stream.',
  payroll: 'Includes 50 employees, then $25 for each additional group of five.',
  third_party: 'Provider charges pass through at cost; unknown costs stay excluded.',
  third_party_record: 'Provider cost per record includes 20% handling.',
  phase_addon: 'An agreed flat add-on for this phase; earlier terms stay unchanged.',
  phase_user: 'One agreed rate per additional user for this phase.',
};

export function monthlyChargeReason(charge: MonthlyCharge): string { return ruleReasons[charge.ruleId]; }

/** Only explicit activity IDs associate project evidence with a charge. */
export function monthlyRowEvidence(row: PricingMonthlyRow, activities: MonthlyActivity[] = []): MonthlyEvidence[] {
  const charges = row.simple?.charges ?? [];
  const ids = new Set(charges.flatMap(charge => charge.activityIds ?? []));
  const all = [...charges.map(charge => charge.evidence), ...activities.filter(activity => ids.has(activity.id)).map(activity => activity.evidence)];
  const seen = new Set<string>();
  return all.filter(evidence => {
    if (!evidence?.source?.trim() || !evidence.quote?.trim()) return false;
    const key = JSON.stringify([evidence.source, evidence.quote]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
